import React, { useEffect, useReducer, useState } from "react";
import openSocket from "../../services/socket-io";
import {
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TicketSubjectModal from "../../components/TicketSubjectModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKET_SUBJECTS") {
    const ticketSubjects = action.payload;
    const nextState = [...state];

    ticketSubjects.forEach(ticketSubject => {
      const ticketSubjectIndex = nextState.findIndex(
        item => item.id === ticketSubject.id
      );
      if (ticketSubjectIndex !== -1) {
        nextState[ticketSubjectIndex] = ticketSubject;
      } else {
        nextState.push(ticketSubject);
      }
    });

    return nextState;
  }

  if (action.type === "UPSERT_TICKET_SUBJECT") {
    const ticketSubject = action.payload;
    const ticketSubjectIndex = state.findIndex(
      item => item.id === ticketSubject.id
    );

    if (ticketSubjectIndex !== -1) {
      state[ticketSubjectIndex] = ticketSubject;
      return [...state];
    }

    return [ticketSubject, ...state];
  }

  if (action.type === "DELETE_TICKET_SUBJECT") {
    return state.filter(item => item.id !== action.payload);
  }

  return state;
};

const TicketSubjects = () => {
  const classes = useStyles();
  const [ticketSubjects, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [ticketSubjectModalOpen, setTicketSubjectModalOpen] = useState(false);
  const [selectedTicketSubject, setSelectedTicketSubject] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ticket-subjects");
        dispatch({ type: "LOAD_TICKET_SUBJECTS", payload: data });
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const socket = openSocket();

    socket.on("ticketSubject", data => {
      if (data.action === "create" || data.action === "update") {
        dispatch({ type: "UPSERT_TICKET_SUBJECT", payload: data.ticketSubject });
      }

      if (data.action === "delete") {
        dispatch({
          type: "DELETE_TICKET_SUBJECT",
          payload: data.ticketSubjectId
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenTicketSubjectModal = () => {
    setSelectedTicketSubject(null);
    setTicketSubjectModalOpen(true);
  };

  const handleCloseTicketSubjectModal = () => {
    setSelectedTicketSubject(null);
    setTicketSubjectModalOpen(false);
  };

  const handleEditTicketSubject = ticketSubject => {
    setSelectedTicketSubject(ticketSubject);
    setTicketSubjectModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedTicketSubject(null);
  };

  const handleDeleteTicketSubject = async ticketSubjectId => {
    try {
      await api.delete(`/ticket-subjects/${ticketSubjectId}`);
      toast.success(i18n.t("ticketSubjects.toasts.deleted"));
    } catch (error) {
      toastError(error);
    }
    setSelectedTicketSubject(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedTicketSubject
            ? i18n.t("ticketSubjects.confirmationModal.deleteTitle", {
                name: selectedTicketSubject.name
              })
            : ""
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteTicketSubject(selectedTicketSubject.id)}
      >
        {i18n.t("ticketSubjects.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <TicketSubjectModal
        open={ticketSubjectModalOpen}
        onClose={handleCloseTicketSubjectModal}
        ticketSubjectId={selectedTicketSubject?.id}
      />
      <MainHeader>
        <Title>{i18n.t("ticketSubjects.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" onClick={handleOpenTicketSubjectModal}>
            {i18n.t("ticketSubjects.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">ID</TableCell>
              <TableCell align="center">{i18n.t("ticketSubjects.table.subject")}</TableCell>
              <TableCell align="center">{i18n.t("ticketSubjects.table.status")}</TableCell>
              <TableCell align="center">{i18n.t("ticketSubjects.table.dashboard")}</TableCell>
              <TableCell align="center">{i18n.t("ticketSubjects.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ticketSubjects.map(ticketSubject => (
              <TableRow key={ticketSubject.id}>
                <TableCell align="center">#{ticketSubject.id}</TableCell>
                <TableCell align="center">{ticketSubject.name}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={
                      ticketSubject.isActive
                        ? i18n.t("ticketSubjects.status.active")
                        : i18n.t("ticketSubjects.status.inactive")
                    }
                    color={ticketSubject.isActive ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={
                      ticketSubject.countInDashboard
                        ? i18n.t("ticketSubjects.dashboard.count")
                        : i18n.t("ticketSubjects.dashboard.ignore")
                    }
                    color={ticketSubject.countInDashboard ? "primary" : "default"}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleEditTicketSubject(ticketSubject)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedTicketSubject(ticketSubject);
                      setConfirmModalOpen(true);
                    }}
                  >
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={5} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default TicketSubjects;
